import { Alert, Spinner } from '@billda/design-system';
import { GangParticipant } from '../../types/gangCreationTypes';
import { useGangMemberSelection } from '../../hooks/useGangMemberSelection';
import { GangMemberSearchInput } from '../molecules/GangMemberSearchInput';
import { GangMemberSearchResults } from '../molecules/GangMemberSearchResults';
import { GangMemberSelectedList } from '../molecules/GangMemberSelectedList';

interface GangMemberSelectionProps {
  selectedIds: string[];
  participants: Record<string, GangParticipant>;
  onSelectUser: (user: {
    id: string;
    fullName: string;
    email: string;
    roles?: string[];
  }) => void;
  onRemoveUser: (userId: string) => void;
  excludeUserIds?: string[];
  maxSelections?: number;
  placeholder?: string;
  isLeaderSelection?: boolean;
  /** IDs of members that exist in the database (from initial gang data) */
  persistedMemberIds?: string[];
  /** When false, hide remove button for persisted members */
  isRemovalEnabled?: boolean;
}

export const GangMemberSelection = ({
  selectedIds,
  participants,
  onSelectUser,
  onRemoveUser,
  excludeUserIds,
  maxSelections,
  placeholder = 'Search users by name or email (minimum 3 characters)',
  isLeaderSelection = false,
  persistedMemberIds,
  isRemovalEnabled,
}: GangMemberSelectionProps) => {
  const {
    searchQuery,
    setSearchQuery,
    users,
    isLoading,
    searchError,
    shouldPreloadMore,
    isPreloadingMore,
    showPreloadIndicator,
    canLoadMore,
    handleLoadMore,
    canSelectMore,
    handleSelectUser,
    selectedParticipants,
  } = useGangMemberSelection({
    selectedIds,
    participants,
    onSelectUser,
    excludeUserIds,
    maxSelections,
  });

  if (searchError) {
    return (
      <Alert severity="error" className="mb-4">
        Failed to search users. Please try again.
      </Alert>
    );
  }

  return (
    <div>
      <fieldset className="w-full border-0 p-0">
        <div className="mb-4 rounded-xl border border-border-default bg-surface-default p-4 shadow-card">
          <div>
            <h6 className="type-h6 mb-2">Available Users</h6>

            <div
              data-testid="auto-preload-flag"
              className={showPreloadIndicator ? 'block' : 'hidden'}
            />

            <GangMemberSearchInput
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder={placeholder}
              isLoading={isLoading}
            />

            {!canSelectMore && users.length > 0 && (
              <Alert severity="info" className="mb-4">
                Maximum number of users selected ({maxSelections}). Remove some
                users to select others.
              </Alert>
            )}

            <GangMemberSearchResults
              searchQuery={searchQuery}
              users={users}
              isLoading={isLoading}
              canSelectMore={canSelectMore}
              onSelectUser={handleSelectUser}
              onLoadMore={handleLoadMore}
              canLoadMore={canLoadMore}
            />

            {(shouldPreloadMore ||
              isPreloadingMore ||
              showPreloadIndicator) && (
              <div className="mt-2 flex items-center justify-center gap-2">
                {isLoading && <Spinner size={16} />}
                <span
                  className="type-caption text-content-secondary"
                  data-testid="load-more-results-indicator"
                >
                  Loading more results...
                </span>
              </div>
            )}

            <GangMemberSelectedList
              participants={selectedParticipants}
              onRemoveUser={onRemoveUser}
              isLeaderSelection={isLeaderSelection}
              maxSelections={maxSelections}
              persistedMemberIds={persistedMemberIds}
              isRemovalEnabled={isRemovalEnabled}
            />
          </div>
        </div>
      </fieldset>
    </div>
  );
};

export default GangMemberSelection;
